export type PreviewFieldType = 'money' | 'number' | 'percent' | 'text';
export type PreviewReturnType = Exclude<PreviewFieldType, 'text'>;
export type PreviewRounding = 'half_up' | 'down';
export type PreviewTaxMode = 'exclude_tax' | 'include_tax';
export interface PreviewFormulaOption {
  formulaKey: string;
  label: string;
  enabled?: boolean;
}
export type PreviewDependencyGraph = Record<string, readonly string[]>;

export interface PreviewFieldOption {
  fieldKey: string;
  label: string;
  value: number | string | null;
  type: PreviewFieldType;
  enabled: boolean;
}

export type PreviewOperator = 'add' | 'subtract' | 'multiply' | 'divide';
export type PreviewFunction = 'abs' | 'round';
export type PreviewToken =
  | { kind: 'field'; fieldKey: string }
  | { kind: 'number'; value: number | null }
  | { kind: 'operator'; operator: PreviewOperator }
  | { kind: 'function'; function: PreviewFunction };

export interface FormulaDraftPreview {
  formulaKey: string;
  tokens: PreviewToken[];
  precision: number;
  returnType: PreviewReturnType;
  rounding: PreviewRounding;
  taxMode: PreviewTaxMode;
  taxRateBps: number;
  dependencyFormulaKeys: string[];
}

export interface PreviewValidation {
  ok: boolean;
  revision: number;
  errors: string[];
}

export interface PreviewCalculation {
  ok: boolean;
  revision: number;
  operationId: string;
  value?: number;
  reason?: string;
}

export interface PublishedFormulaPreview {
  formulaVersion: string;
  draft: FormulaDraftPreview;
}

/** Same-process Disposable proof only; it is neither a server signature nor API credential. */
export interface DisposableFormulaPublicationEvidence {
  readonly fieldKey: string;
  readonly formulaVersion: string;
  readonly revision: number;
}

const issuedPublicationEvidence = new WeakSet<object>();
const publishedPreviewObjects = new WeakSet<object>();
const validatedPreviewObjects = new WeakSet<object>();
const requestedPreviewObjects = new WeakSet<object>();
const calculatedPreviewObjects = new WeakSet<object>();

export function issueDisposablePublicationEvidence(
  fieldKey: string,
  preview: FormulaStudioPreview,
): DisposableFormulaPublicationEvidence {
  if (
    !fieldKey.trim() ||
    !preview.published ||
    !publishedPreviewObjects.has(preview.published)
  ) {
    throw new Error('当前草稿没有可发布的 Disposable 状态机证明');
  }
  const evidence: DisposableFormulaPublicationEvidence = Object.freeze({
    fieldKey,
    formulaVersion: preview.published.formulaVersion,
    revision: preview.revision,
  });
  issuedPublicationEvidence.add(evidence);
  return evidence;
}

export function consumeDisposablePublicationEvidence(
  evidence: unknown,
  fieldKey: string,
  formulaVersion: string,
): boolean {
  if (
    !evidence ||
    typeof evidence !== 'object' ||
    !issuedPublicationEvidence.has(evidence)
  )
    return false;
  const candidate = evidence as DisposableFormulaPublicationEvidence;
  if (
    candidate.fieldKey !== fieldKey ||
    candidate.formulaVersion !== formulaVersion ||
    !Number.isSafeInteger(candidate.revision) ||
    candidate.revision < 1
  )
    return false;
  return true;
}

export interface FormulaStudioPreview {
  readonly boundary: '前端试算/非正式resolver';
  readonly fields: readonly PreviewFieldOption[];
  readonly revision: number;
  readonly draft: FormulaDraftPreview;
  readonly validation: PreviewValidation | null;
  readonly calculation: PreviewCalculation | null;
  readonly published: PublishedFormulaPreview | null;
  readonly nextFormulaVersion: number;
  readonly nextRequestSequence: number;
  readonly formulaOptions: readonly PreviewFormulaOption[];
  readonly dependencyGraph: PreviewDependencyGraph;
}

export interface HistoricalPreviewSnapshot {
  fieldKey: string;
  definitionVersion: string;
  formulaVersion: string;
  value: number | string | null;
}

const numericTypes = new Set<PreviewFieldType>(['money', 'number', 'percent']);
const operatorSet = new Set<PreviewOperator>([
  'add',
  'subtract',
  'multiply',
  'divide',
]);
const functionSet = new Set<PreviewFunction>(['abs', 'round']);
const maxAbsolutePreviewValue = 1_000_000_000_000;

function baseDraft(formulaKey: string): FormulaDraftPreview {
  return {
    formulaKey,
    tokens: [],
    returnType: 'money',
    precision: 2,
    rounding: 'half_up',
    taxMode: 'exclude_tax',
    taxRateBps: 1300,
    dependencyFormulaKeys: [],
  };
}

export function createFormulaStudioPreview(
  fields: readonly PreviewFieldOption[],
  formulaKey = 'preview_formula',
  context: {
    formulaOptions?: readonly PreviewFormulaOption[];
    dependencyGraph?: PreviewDependencyGraph;
    nextFormulaVersion?: number;
  } = {},
): FormulaStudioPreview {
  const nextFormulaVersion = context.nextFormulaVersion;
  return {
    boundary: '前端试算/非正式resolver',
    fields: fields.map((field) => ({ ...field })),
    revision: 1,
    draft: baseDraft(formulaKey),
    validation: null,
    calculation: null,
    published: null,
    nextFormulaVersion:
      Number.isSafeInteger(nextFormulaVersion) && (nextFormulaVersion ?? 0) > 0
        ? (nextFormulaVersion ?? 1)
        : 1,
    nextRequestSequence: 1,
    formulaOptions: context.formulaOptions?.map((item) => ({ ...item })) ?? [],
    dependencyGraph: structuredClone(context.dependencyGraph ?? {}),
  };
}

function edited(
  preview: FormulaStudioPreview,
  draft: FormulaDraftPreview,
): FormulaStudioPreview {
  if (preview.published)
    throw new Error('已发布演示公式不可原地编辑，请创建新草稿');
  return {
    ...preview,
    revision: preview.revision + 1,
    draft,
    validation: null,
    calculation: null,
  };
}

function requireField(preview: FormulaStudioPreview, fieldKey: string) {
  const field = preview.fields.find((item) => item.fieldKey === fieldKey);
  if (!field) throw new Error('字段不在白名单');
  return field;
}

export function appendFieldToken(
  preview: FormulaStudioPreview,
  fieldKey: string,
) {
  requireField(preview, fieldKey);
  return edited(preview, {
    ...preview.draft,
    tokens: [...preview.draft.tokens, { kind: 'field', fieldKey }],
  });
}

export function appendNumberToken(
  preview: FormulaStudioPreview,
  value: number | null,
) {
  if (value === null || !Number.isFinite(value))
    throw new Error('常量必须为有限数字');
  return edited(preview, {
    ...preview.draft,
    tokens: [...preview.draft.tokens, { kind: 'number', value }],
  });
}

export function appendOperatorToken(
  preview: FormulaStudioPreview,
  operator: PreviewOperator,
) {
  if (!operatorSet.has(operator)) throw new Error('运算符不在白名单');
  return edited(preview, {
    ...preview.draft,
    tokens: [...preview.draft.tokens, { kind: 'operator', operator }],
  });
}

export function appendFunctionToken(
  preview: FormulaStudioPreview,
  fn: PreviewFunction,
) {
  if (!functionSet.has(fn)) throw new Error('函数不在白名单');
  return edited(preview, {
    ...preview.draft,
    tokens: [...preview.draft.tokens, { kind: 'function', function: fn }],
  });
}

export function undoPreviewToken(preview: FormulaStudioPreview) {
  if (!preview.draft.tokens.length) return preview;
  return edited(preview, {
    ...preview.draft,
    tokens: preview.draft.tokens.slice(0, -1),
  });
}

export function clearPreviewTokens(preview: FormulaStudioPreview) {
  if (!preview.draft.tokens.length) return preview;
  return edited(preview, { ...preview.draft, tokens: [] });
}

export function configurePreviewDraft(
  preview: FormulaStudioPreview,
  change: Partial<
    Pick<
      FormulaDraftPreview,
      | 'returnType'
      | 'precision'
      | 'rounding'
      | 'taxMode'
      | 'taxRateBps'
      | 'dependencyFormulaKeys'
    >
  >,
) {
  return edited(preview, {
    ...preview.draft,
    ...change,
    dependencyFormulaKeys: change.dependencyFormulaKeys
      ? [...change.dependencyFormulaKeys]
      : preview.draft.dependencyFormulaKeys,
  });
}

function hasDependencyCycle(
  key: string,
  direct: readonly string[],
  graph: PreviewDependencyGraph,
): boolean {
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (node: string): boolean => {
    if (visiting.has(node)) return true;
    if (visited.has(node)) return false;
    visiting.add(node);
    const next = node === key ? direct : (graph[node] ?? []);
    const found = next.some(visit);
    visiting.delete(node);
    visited.add(node);
    return found;
  };
  return visit(key);
}

function validateTokens(preview: FormulaStudioPreview): string[] {
  const errors: string[] = [];
  const { draft } = preview;
  if (!draft.formulaKey.trim()) errors.push('formula key 不能为空');
  if (
    !Number.isInteger(draft.precision) ||
    draft.precision < 0 ||
    draft.precision > 6
  )
    errors.push('预览精度必须是 0 到 6 的整数');
  if (
    !Number.isInteger(draft.taxRateBps) ||
    draft.taxRateBps < 0 ||
    draft.taxRateBps > 10_000
  )
    errors.push('预览税率必须为 0 到 10000 bps');
  if (!['money', 'number', 'percent'].includes(draft.returnType))
    errors.push('返回类型无效');
  if (!['half_up', 'down'].includes(draft.rounding))
    errors.push('舍入规则无效');
  if (!['exclude_tax', 'include_tax'].includes(draft.taxMode))
    errors.push('税务语义无效');
  for (const key of draft.dependencyFormulaKeys) {
    const dependency = preview.formulaOptions.find(
      (item) => item.formulaKey === key,
    );
    if (!dependency) errors.push(`依赖不存在：${key}`);
    else if (dependency.enabled === false) errors.push(`依赖已停用：${key}`);
  }
  const knownFormulaKeys = new Set(
    preview.formulaOptions.map((item) => item.formulaKey),
  );
  const reachable = new Set<string>();
  const collect = (node: string) => {
    if (reachable.has(node)) return;
    reachable.add(node);
    (preview.dependencyGraph[node] ?? []).forEach(collect);
  };
  draft.dependencyFormulaKeys.forEach(collect);
  for (const key of reachable)
    if (!knownFormulaKeys.has(key) && key !== draft.formulaKey)
      errors.push(`依赖图节点不存在：${key}`);
  if (
    hasDependencyCycle(
      draft.formulaKey,
      draft.dependencyFormulaKeys,
      preview.dependencyGraph,
    )
  )
    errors.push('检测到循环依赖');
  if (!draft.tokens.length) errors.push('至少需要一个 token');

  let expectsOperand = true;
  for (const token of draft.tokens) {
    if (!token || typeof token !== 'object' || !('kind' in token)) {
      errors.push('token 序列无效');
      continue;
    }
    if (token.kind === 'operator') {
      if (expectsOperand || !operatorSet.has(token.operator))
        errors.push('运算符前缺少操作数或名称无效');
      expectsOperand = true;
      continue;
    }
    if (token.kind === 'function') {
      if (!expectsOperand || !functionSet.has(token.function))
        errors.push('函数位置或名称无效');
      continue;
    }
    if (token.kind !== 'number' && token.kind !== 'field') {
      errors.push('token 类型无效');
      continue;
    }
    if (!expectsOperand) errors.push('操作数之间缺少运算符');
    expectsOperand = false;
    if (token.kind === 'number') {
      if (token.value === null || !Number.isFinite(token.value))
        errors.push('常量必须为有限数字');
      continue;
    }
    const field = preview.fields.find(
      (item) => item.fieldKey === token.fieldKey,
    );
    if (!field) errors.push(`缺少字段 ${token.fieldKey}`);
    else if (!field.enabled) errors.push(`依赖字段已停用：${token.fieldKey}`);
    else if (!numericTypes.has(field.type))
      errors.push(`字段类型不兼容：${token.fieldKey}`);
  }
  if (expectsOperand && draft.tokens.length)
    errors.push('表达式不能以运算符结束');
  return errors;
}

export function validatePreviewDraft(
  preview: FormulaStudioPreview,
): FormulaStudioPreview {
  const errors = validateTokens(preview);
  const next = {
    ...preview,
    validation: { ok: errors.length === 0, revision: preview.revision, errors },
    calculation: null,
  };
  if (next.validation.ok) validatedPreviewObjects.add(next);
  return next;
}

export function requestPreviewCalculation(preview: FormulaStudioPreview) {
  if (
    !validatedPreviewObjects.has(preview) ||
    !preview.validation?.ok ||
    preview.validation.revision !== preview.revision
  )
    throw new Error('请先完成当前草稿验证');
  const operationId = `preview-calc-r${preview.revision}-${preview.nextRequestSequence}`;
  const next = {
    ...preview,
    nextRequestSequence: preview.nextRequestSequence + 1,
    calculation: {
      ok: false as const,
      revision: preview.revision,
      operationId,
      reason: '试算中',
    },
  };
  validatedPreviewObjects.add(next);
  requestedPreviewObjects.add(next);
  return { operationId, preview: next };
}

export function applyPreviewCalculation(
  preview: FormulaStudioPreview,
  operationId: string,
  result: Omit<PreviewCalculation, 'revision' | 'operationId'>,
): FormulaStudioPreview {
  if (
    !requestedPreviewObjects.has(preview) ||
    preview.calculation?.operationId !== operationId ||
    preview.calculation.revision !== preview.revision
  )
    return preview;
  const value = result.value;
  if (
    result.ok &&
    (typeof value !== 'number' ||
      !Number.isFinite(value) ||
      Math.abs(value) > maxAbsolutePreviewValue ||
      (preview.draft.returnType === 'percent' && (value < 0 || value > 1)))
  ) {
    return {
      ...preview,
      calculation: {
        ok: false,
        operationId,
        revision: preview.revision,
        reason: '异步试算结果无效',
      },
    };
  }
  if (result.ok) {
    const next = {
      ...preview,
      calculation: {
        ok: true as const,
        value: rounded(
          value as number,
          preview.draft.precision,
          preview.draft.rounding,
        ),
        operationId,
        revision: preview.revision,
      },
    };
    validatedPreviewObjects.add(next);
    calculatedPreviewObjects.add(next);
    return next;
  }
  return {
    ...preview,
    calculation: { ...result, operationId, revision: preview.revision },
  };
}

function rounded(
  value: number,
  precision: number,
  rounding: PreviewRounding,
): number {
  const factor = 10 ** precision;
  return (
    (rounding === 'down'
      ? Math.trunc(value * factor)
      : Math.round(value * factor)) / factor
  );
}

function evaluateTokens(preview: FormulaStudioPreview): number {
  const tokens = preview.draft.tokens;
  let index = 0;
  const parsePrimary = (): number => {
    const token = tokens[index++];
    if (!token) throw new Error('缺少操作数');
    if (token.kind === 'function') {
      const operand = parsePrimary();
      return token.function === 'abs'
        ? Math.abs(operand)
        : rounded(operand, preview.draft.precision, preview.draft.rounding);
    }
    if (token.kind === 'number') {
      if (token.value === null) throw new Error('空值不能参与试算');
      return token.value;
    }
    if (token.kind === 'field') {
      const field = requireField(preview, token.fieldKey);
      if (typeof field.value !== 'number' || !Number.isFinite(field.value))
        throw new Error(`字段值无效：${field.fieldKey}`);
      return field.value;
    }
    throw new Error('运算符前缺少操作数');
  };
  const parseProduct = (): number => {
    let value = parsePrimary();
    while (
      tokens[index]?.kind === 'operator' &&
      (tokens[index] as { operator: PreviewOperator }).operator !== 'add' &&
      (tokens[index] as { operator: PreviewOperator }).operator !== 'subtract'
    ) {
      const operator = (tokens[index++] as { operator: PreviewOperator })
        .operator;
      const right = parsePrimary();
      if (operator === 'divide' && right === 0) throw new Error('除零');
      value = operator === 'multiply' ? value * right : value / right;
      if (!Number.isFinite(value) || Math.abs(value) > maxAbsolutePreviewValue)
        throw new Error('试算精度溢出');
    }
    return value;
  };
  let value = parseProduct();
  while (tokens[index]?.kind === 'operator') {
    const operator = (tokens[index++] as { operator: PreviewOperator })
      .operator;
    const right = parseProduct();
    value = operator === 'add' ? value + right : value - right;
    if (!Number.isFinite(value) || Math.abs(value) > maxAbsolutePreviewValue)
      throw new Error('试算精度溢出');
  }
  if (index !== tokens.length) throw new Error('token 序列无效');
  const taxed =
    preview.draft.taxMode === 'include_tax'
      ? value * (1 + preview.draft.taxRateBps / 10_000)
      : value;
  if (preview.draft.returnType === 'percent' && (taxed < 0 || taxed > 1))
    throw new Error('percent 返回类型要求试算值在 0 到 1');
  return rounded(taxed, preview.draft.precision, preview.draft.rounding);
}

export function calculatePreviewNow(
  preview: FormulaStudioPreview,
  operationId: string,
): FormulaStudioPreview {
  try {
    return applyPreviewCalculation(preview, operationId, {
      ok: true,
      value: evaluateTokens(preview),
    });
  } catch (error) {
    return applyPreviewCalculation(preview, operationId, {
      ok: false,
      reason: error instanceof Error ? error.message : '试算失败',
    });
  }
}

export function createNextPreviewDraft(
  preview: FormulaStudioPreview,
): FormulaStudioPreview {
  if (!preview.published) throw new Error('仅已发布演示公式可创建新草稿');
  return {
    ...preview,
    revision: preview.revision + 1,
    draft: baseDraft(preview.draft.formulaKey),
    validation: null,
    calculation: null,
    published: null,
  };
}

export function publishPreviewFormula(
  preview: FormulaStudioPreview,
): FormulaStudioPreview {
  if (preview.published) throw new Error('演示公式已发布，不可重复发布');
  if (
    !validatedPreviewObjects.has(preview) ||
    !preview.validation?.ok ||
    preview.validation.revision !== preview.revision
  )
    throw new Error('请先验证当前草稿');
  if (
    !calculatedPreviewObjects.has(preview) ||
    !preview.calculation?.ok ||
    preview.calculation.revision !== preview.revision
  )
    throw new Error('请先完成当前草稿试算');
  const published: PublishedFormulaPreview = {
    formulaVersion: `preview-v${preview.nextFormulaVersion}`,
    draft: structuredClone(preview.draft),
  };
  publishedPreviewObjects.add(published);
  return {
    ...preview,
    published,
    nextFormulaVersion: preview.nextFormulaVersion + 1,
  };
}

export function createHistoricalPreviewSnapshot(
  snapshot: HistoricalPreviewSnapshot,
): HistoricalPreviewSnapshot {
  return structuredClone(snapshot);
}
